import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReactComponent as ChevronRight } from '../../assets/images/icons/chevron_right.svg';
import { useLocale } from '../../contexts/LocaleContext';
import * as events from '../../lib/analytics/events';
import { hardwareFirstSizeName } from '../../lib/hardwareFirst';
import { colorAndDesignVariantName, variantWithOptions } from '../../lib/product';
import {
  BandSizeDetails,
  WeightUnit,
  f3BandSizeOptions,
  getDefaultMeasurementUnit,
  getDefaultWeightUnit,
  sizeDetailsForSizeOption,
} from '../../lib/size';
import * as types from '../../types';
import ColorOptionButton from '../../views/ProductDetails/components/ColorOptionButton';
import HardwareFirstSizingModal from './HardwareFirstSizingModal';
import styles from './HardwareFirstVariantStep.module.scss';

// the design lists the largest band first, so a shopper scanning down lands on their dog sooner
const SIZES_LARGEST_FIRST = [...f3BandSizeOptions].reverse();

// "Extra Small"/"Extra Large" rather than the shared "X Small"/"X Large" the classic pdp uses
interface HardwareFirstVariantStepProps {
  changeVariantOptions: (newOptions: Partial<types.IVariantOptions>) => void;
  /** False until the shopper picks a size, so no card starts out looking chosen. */
  hasUpdatedSize: boolean;
  selectedVariant: types.IVariant;
  variants: types.IVariant[];
}

/**
 * Size and colour picker for the hardware-first plp's Series 3+ step. The classic pdp's
 * SizeOptions/ColorOptions render a different card, unit toggle and label set, so this is a
 * separate surface rather than a restyle of components the control arm also renders.
 */
export default function HardwareFirstVariantStep({
  changeVariantOptions,
  hasUpdatedSize,
  selectedVariant,
  variants,
}: HardwareFirstVariantStepProps) {
  const { t } = useTranslation();
  const { country } = useLocale();
  const blockedSkus = useSelector((state: types.AppState) => state.config.siteConfig.blockedOutOfStockSkus || []);
  const measurementUnit = getDefaultMeasurementUnit(country);
  // the toggle starts on the country's unit and only changes the weight ranges on the cards
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(() => getDefaultWeightUnit(country));
  const weightUnits: WeightUnit[] = ['lbs', 'kg'];

  const colorOptions = useMemo(
    () =>
      variants
        .filter((v) => v.options.size === selectedVariant.options.size && v.bandMaker === types.BandMaker.Fi)
        .map((v) => v.options.color),
    [selectedVariant.options.size, variants],
  );

  const sizes = useMemo(
    () =>
      SIZES_LARGEST_FIRST.filter((size) => variants.some((v) => v.options.size === size))
        .map((size) => {
          const variant = variantWithOptions(variants, { ...selectedVariant.options, size });
          return {
            size,
            available: !!variant && !blockedSkus.includes(variant.sku),
            // this step only offers Fi originals, so the details always come from the F3 table
            details: sizeDetailsForSizeOption({
              bandSeries: types.BandSeries.F3,
              sizeOption: size,
              bandMaker: types.BandMaker.Fi,
              measurementUnit,
              weightUnit,
            }),
          };
        })
        .filter((entry): entry is typeof entry & { details: BandSizeDetails } => !!entry.details),
    [blockedSkus, measurementUnit, selectedVariant, variants, weightUnit],
  );

  return (
    <div className={styles.step}>
      <section className={styles.colorSection}>
        <h2 className={styles.sectionTitle}>{t('hardware_first_pick_your_color')}</h2>
        <div className={styles.swatchRow}>
          {colorOptions.map((colorOption) => (
            <ColorOptionButton
              available={true}
              className={classNames(styles.swatch, {
                [styles.swatchSelected]: colorOption === selectedVariant.options.color,
              })}
              colorOption={colorOption}
              key={colorOption}
              onSelect={(color) => changeVariantOptions({ color })}
              selected={colorOption === selectedVariant.options.color}
            />
          ))}
        </div>
        <div className={styles.colorName}>{colorAndDesignVariantName(selectedVariant.options.color)}</div>
      </section>

      <section className={styles.sizeSection}>
        <div className={styles.sizeHeader}>
          <h2 className={styles.sectionTitle}>{t('pick_your_size_title')}</h2>
          <div aria-label={t('hardware_first_weight_unit')} className={styles.unitToggle} role="group">
            {weightUnits.map((unit) => (
              <button
                aria-pressed={unit === weightUnit}
                className={classNames(styles.unitOption, { [styles.unitOptionSelected]: unit === weightUnit })}
                key={unit}
                onClick={() => setWeightUnit(unit)}
                type="button"
              >
                {t(unit === 'kg' ? 'hardware_first_unit_kgs' : 'hardware_first_unit_lbs')}
              </button>
            ))}
          </div>
        </div>
        <HardwareFirstSizingModal
          onOpen={() => events.series3CollarWhatSizeIsRight()}
          onSelect={(size: types.SizeOption) => changeVariantOptions({ size })}
          trigger={
            <button className={styles.guideRow} type="button">
              <span className={styles.guideLabel}>{t('hardware_first_which_size_is_right')}</span>
              <ChevronRight className={styles.guideChevron} />
            </button>
          }
        />
        <div className={styles.sizeList}>
          {sizes.map(({ size, available, details }) => (
            <button
              className={classNames(styles.sizeCard, {
                [styles.sizeCardSelected]: hasUpdatedSize && size === selectedVariant.options.size,
                [styles.sizeCardUnavailable]: !available,
              })}
              disabled={!available}
              key={size}
              onClick={() => changeVariantOptions({ size })}
              type="button"
            >
              <span className={styles.sizeCardTop}>
                <span className={styles.sizeName}>{hardwareFirstSizeName(t, size, details.sizeName)}</span>
                <span className={styles.sizeWeight}>{details.weightRange}</span>
              </span>
              {details.dogExamples && (
                <span className={styles.sizeExamples}>
                  {t('hardware_first_best_for_dogs_similar_to', { examples: details.dogExamples })}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
